import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Sparkles } from 'lucide-react';

import { PageHeader } from '@/components/app-shell/PageHeader';
import { useProducts } from '@/features/product-scan/useProducts';

import { CustomerDrawer } from './drawer/CustomerDrawer';
import { INSIGHT_TYPES } from './campaigns-config';
import { buildQuickInsights, type QuickInsight } from './insights/quick-insights';
import { ProductInsightsRow } from './insights/ProductInsightsRow';
import { CustomerLedger } from './ledger/CustomerLedger';
import {
  EMPTY_COLUMN_FILTERS,
  EMPTY_FILTERS,
  type ColumnFilters,
  type LedgerFilters,
  filterCustomers,
} from './ledger/ledger-filter';
import { LedgerToolbar } from './ledger/LedgerToolbar';
import { SelectionToolbar } from './selection/SelectionToolbar';

import { useReferralsStore } from '@/features/referrals/referrals-store';

import { persistCampaign } from './campaigns-api';
import { CampaignSelector } from './CampaignSelector';
import { CampaignDetailView } from './outcomes/CampaignDetailView';
import { useCampaigns } from './outcomes/useCampaign';
import { useToast } from './toast-context';
import { useCampaignCustomers } from './useCampaignCustomers';
import { buildCsv, csvFilename, downloadCsv } from './wizard/csv-export';
import { CampaignWizard } from './wizard/CampaignWizard';
import type { WizardInit } from './wizard/campaign-state';
import type { Customer } from '@/features/intelligence-dashboard/customer.schema';
import type { InsightType } from '@/features/intelligence-dashboard/insight.types';
import type { Product } from '@/features/product-scan/product-scan.schema';

/**
 * Campaigns page — customer intelligence ledger, detail drawer, selection
 * toolbar, and the New Campaign wizard with CSV export (Story SCRUM-203).
 *
 * Subtask D (SCRUM-208) adds the floating selection toolbar and the Product
 * Insights row: the toolbar's Product Insights button reveals ready-to-launch
 * insight cards, and Apply Insight filters the ledger (insight + product match).
 * The wizard (E) and CSV export (F) build on the page state established here.
 */
interface CampaignsPageProps {
  /** SCRUM-261: customer id to deep-link the detail drawer open on mount. */
  initialInspectId?: string | null;
  /** Called once after the deep-link id has been applied, so the caller can clear it. */
  onInspectConsumed?: () => void;
  /**
   * SCRUM-327: wizard init to deep-link the campaign wizard open on mount
   * (Dashboard "Start outreach" — customer as target seed + product staged).
   */
  initialWizardInit?: WizardInit | null;
  /** Called once after the wizard deep-link has been applied. */
  onWizardInitConsumed?: () => void;
  /**
   * SCRUM-847: column filters to seed on mount from a Dashboard KPI drill-through
   * (e.g. `{ signals: 'relgap' }` or `{ last: { max: 30 } }`). Seeded like the other
   * deep-links; consumed one-shot so a later normal visit is not pre-filtered.
   */
  initialColumnFilters?: ColumnFilters | null;
  /** Called once after the column-filter deep-link has been applied. */
  onColumnFiltersConsumed?: () => void;
  /**
   * SCRUM-389 (v7 teller-dashboard): reuse this page as the Teller's Customer
   * Dashboard with ALL campaign affordances stripped — no utility strip, no
   * product-insights panel, no selection/batch bar, no wizard; the drawer
   * keeps the refer flow only.
   */
  tellerMode?: boolean;
}

function CampaignsPageInner({
  initialInspectId = null,
  onInspectConsumed,
  initialWizardInit = null,
  onWizardInitConsumed,
  initialColumnFilters = null,
  onColumnFiltersConsumed,
  tellerMode = false,
}: CampaignsPageProps) {
  const { data: customers, isLoading, error } = useCampaignCustomers();
  const { data: products } = useProducts();
  const { showToast } = useToast();

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Seed from the deep-link id (SCRUM-261). Correct because this page unmounts on
  // every nav switch, so "mount" == "each visit" — there is no stale-state carryover.
  const [inspectorId, setInspectorId] = useState<string | null>(initialInspectId);

  // One-shot: notify the parent (AppShell) that the deep-link id was consumed, so it
  // clears its pending id and a normal later visit does not auto-open the drawer.
  // useRef guard avoids re-firing and keeps exhaustive-deps satisfied (no disable).
  const consumedRef = useRef(false);
  useEffect(() => {
    if (initialInspectId && !consumedRef.current) {
      consumedRef.current = true;
      onInspectConsumed?.();
    }
  }, [initialInspectId, onInspectConsumed]);
  const [insightsOpen, setInsightsOpen] = useState(false);
  const [appliedInsight, setAppliedInsight] = useState<QuickInsight | null>(null);
  /**
   * SCRUM-867 per-column filter state. Before this there was NO filter state at
   * all — `filters` was derived purely from `search` + `appliedInsight` — so the
   * column filters introduce it rather than extend it.
   *
   * OWNERSHIP (the trap): `appliedInsight` keeps sole ownership of the Product
   * Insights panel's insight AND its product (which drives the MATCH column).
   * This state owns only the column selections, including its own `signals`
   * field. Neither control writes the other's, so the two cannot fight; the two
   * signal constraints AND together, which is the intended union.
   *
   * Defaults are the EMPTY_FILTERS values, so a page that never touches the
   * column filters (the teller Customer Dashboard) behaves exactly as before.
   */
  // SCRUM-847: a KPI drill-through seeds the column filters on mount (from a fresh
  // EMPTY_COLUMN_FILTERS, never merged onto stale state — mount == visit). One-shot
  // consumed below so the URL clears and a later normal visit is not pre-filtered.
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>(
    initialColumnFilters ?? EMPTY_COLUMN_FILTERS,
  );
  // Seeds from the wizard deep-link (SCRUM-327) the same way inspectorId seeds
  // from initialInspectId — mount == visit, so no stale-state carryover.
  const [wizardInit, setWizardInit] = useState<WizardInit | null>(initialWizardInit);

  const wizardConsumedRef = useRef(false);
  useEffect(() => {
    if (initialWizardInit && !wizardConsumedRef.current) {
      wizardConsumedRef.current = true;
      onWizardInitConsumed?.();
    }
  }, [initialWizardInit, onWizardInitConsumed]);

  // SCRUM-847: KPI drill-through — confirm the seeded filter with the "Insight applied"
  // toast and clear the URL param (one-shot). Count is over the whole customer set (the
  // ledger renders page 1 of exactly this population, via the filters useMemo below).
  const columnFiltersConsumedRef = useRef(false);
  useEffect(() => {
    if (!initialColumnFilters || columnFiltersConsumedRef.current) return;
    // Wait for the customer data to load so the toast reports the real population count
    // (the count is 0 while the ledger fetch is in flight). One-shot fires exactly once,
    // after data resolves, then clears the URL param.
    if (!customers) return;
    columnFiltersConsumedRef.current = true;
    const label = initialColumnFilters.last
      ? 'Reached in last 30 days'
      : initialColumnFilters.signals && initialColumnFilters.signals in INSIGHT_TYPES
        ? INSIGHT_TYPES[initialColumnFilters.signals as InsightType].label
        : 'Filter';
    const count = filterCustomers(customers, {
      ...EMPTY_FILTERS,
      ...initialColumnFilters,
    }).length;
    showToast({
      title: 'Insight applied',
      body: `${label} · ${count.toLocaleString('en-US')} customer${count === 1 ? '' : 's'}`,
      variant: 'success',
    });
    onColumnFiltersConsumed?.();
  }, [initialColumnFilters, onColumnFiltersConsumed, customers, showToast]);
  // The campaign whose outcome detail view is open, replacing the customer ledger (SCRUM-72–74).
  const [activeCampaignId, setActiveCampaignId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Persistent, server-backed campaign list for the selector (SCRUM-71) — replaces the
  // session-scoped recent-campaign chips so campaigns stay selectable across sessions.
  const { data: campaigns } = useCampaigns();
  const safeCampaigns = useMemo(() => campaigns ?? [], [campaigns]);

  /**
   * Best-effort campaign persist (SCRUM-531) with a Retry affordance (SCRUM-794). Fired after a
   * successful CSV export; on success it refreshes the server-backed selector (SCRUM-71) so the
   * new campaign appears for outcome tracking. On failure it surfaces a sticky, actionable toast
   * whose Retry re-runs this same attempt with the identical name/product/targets — so the
   * officer can recover without re-running the wizard, and never re-triggers the CSV download.
   *
   * Declared as a plain in-component function (not useCallback) so the Retry closure can
   * reference it recursively without a self-referential dependency, and because it is only ever
   * invoked from event handlers — never a hook dependency. It closes over the stable
   * queryClient/showToast.
   */
  function attemptPersist(name: string, product: Product | null, targets: Customer[]) {
    void persistCampaign(name, product, targets).then((id) => {
      if (id) {
        void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      } else {
        // SCRUM-793: the CSV export succeeded but the campaign did NOT persist, so it won't
        // appear in the selector for outcome tracking. Surface it with a Retry (SCRUM-794);
        // neutral variant — the CSV is the primary artifact and is unaffected.
        showToast({
          body: 'CSV exported. Outcome tracking couldn’t be saved — this campaign won’t appear for tracking.',
          action: {
            label: 'Retry',
            onClick: () => {
              attemptPersist(name, product, targets);
            },
          },
        });
      }
    });
  }

  const safeCustomers = useMemo(() => customers ?? [], [customers]);
  const safeProducts = useMemo(() => products ?? [], [products]);

  const quickInsights = useMemo(
    () => buildQuickInsights(safeCustomers, safeProducts),
    [safeCustomers, safeProducts],
  );

  // Applied Product Insight drives the insight + product-match filters; the
  // SCRUM-867 column filters are layered on top. Order matters only for clarity —
  // the two never write the same field (see the columnFilters comment above).
  const filters: LedgerFilters = useMemo(() => {
    const activeInsights: InsightType[] = appliedInsight ? [appliedInsight.insightKey] : [];
    const productMatches = appliedInsight ? [appliedInsight.product] : [];
    return { ...EMPTY_FILTERS, ...columnFilters, search, activeInsights, productMatches };
  }, [search, appliedInsight, columnFilters]);

  const filteredCount = useMemo(
    () => filterCustomers(safeCustomers, filters).length,
    [safeCustomers, filters],
  );

  const inspectorCustomer = useMemo(
    () => (inspectorId ? (safeCustomers.find((c) => c.id === inspectorId) ?? null) : null),
    [inspectorId, safeCustomers],
  );

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll(ids: string[], allSelected: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const id of ids) next.delete(id);
      } else {
        for (const id of ids) next.add(id);
      }
      return next;
    });
  }

  // v7 (SCRUM-360): the card body toggles the insight — selecting an un-applied
  // card applies it (filters the ledger + minimizes the others); selecting the
  // applied card again clears it. The Apply/Remove buttons call the same path.
  function toggleInsight(qi: QuickInsight) {
    if (appliedInsight?.id === qi.id) {
      setAppliedInsight(null);
      setSelectedIds(new Set());
      return;
    }
    setAppliedInsight(qi);
    setSearch('');
    setSelectedIds(new Set());
    showToast(`Filtered ledger to ${qi.count.toLocaleString('en-US')} matched customers.`);
  }

  // Wizard openers — selection and drawer single-customer.
  function openWizardForSelection() {
    const ids =
      selectedIds.size > 0
        ? [...selectedIds]
        : filterCustomers(safeCustomers, filters).map((c) => c.id);
    setWizardInit({ targetIds: ids });
  }

  function openWizardForCustomer(id: string) {
    setWizardInit({ targetIds: [id] });
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-text-2 text-sm">Loading customers…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-danger text-sm">Failed to load customers: {error.message}</p>
      </div>
    );
  }

  return (
    // gap-4 = the 16px separation between the ledger and the drawer card (Ryan
    // v6 layout (SCRUM-353): a single COLUMN — header, utility strip, quick
    // insights, then the ledger area. The page (<main>) is the only vertical
    // scroller; the ledger column has NO overflow context of its own (D4), and
    // the drawer is an absolutely positioned sibling card inside the relative
    // ledger area (D5).
    <div className="flex flex-col">
      {/* Canonical full-bleed header band (SCRUM-663). */}
      <PageHeader
        title={tellerMode ? 'Customer Dashboard' : 'Campaigns'}
        subtitle={
          tellerMode
            ? 'Browse customers and refer them to a fitting product'
            : 'Customer intelligence ledger · outreach & CSV export'
        }
      />

      {/* Content column (was the page root; the header moved into the band above). */}
      <div className="flex w-full flex-col gap-3 p-6">
        {/* SCRUM-803: the server-backed campaign selector (SCRUM-71) lives at the top of the
            content band — full-width and wrapping — so it is consistently present above BOTH
            the customer ledger and the outcome-detail view. Officer-only (mirrors the ledger
            !tellerMode gating). Moved here from the narrow PageHeader actions slot. */}
        {!tellerMode && (
          <CampaignSelector
            campaigns={safeCampaigns}
            activeId={activeCampaignId}
            onSelect={setActiveCampaignId}
          />
        )}
        {/* SCRUM-72–74: when a campaign is selected, the content area shows its outcome
            detail (summary bar + outcome table + tagging) instead of the customer ledger.
            Officer-only, mirroring the ledger's !tellerMode gating. */}
        {!tellerMode && activeCampaignId ? (
          <CampaignDetailView
            campaignId={activeCampaignId}
            onBack={() => {
              setActiveCampaignId(null);
            }}
          />
        ) : (
          <>
            {/* v6 .campaigns-utility — hidden entirely in teller mode (SCRUM-389). */}
            {!tellerMode && (
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setInsightsOpen((v) => !v);
                  }}
                  aria-pressed={insightsOpen}
                  className={`rounded-tk-md inline-flex h-[34px] items-center gap-2 border px-3 text-xs font-medium tracking-[0.01em] transition-colors ${
                    insightsOpen
                      ? 'bg-brand-dim text-brand-primary border-[var(--border-accent)]'
                      : 'border-border-1 bg-bg-3 text-text-1 hover:bg-bg-4 hover:border-border-2 hover:text-text-0'
                  }`}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Product Insights
                  <span
                    className={`font-mono text-[0.6875rem] ${insightsOpen ? 'text-brand-primary' : 'text-text-3'}`}
                  >
                    {quickInsights.length}
                  </span>
                </button>
                {/* v6 .btn-primary (34px utility variant): WHITE text, weight 600, gold
            gradient; 55% opacity when nothing is selected (D2). */}
                <button
                  type="button"
                  disabled={selectedIds.size === 0}
                  onClick={openWizardForSelection}
                  className={`rounded-tk-md shadow-tk-sm inline-flex h-[34px] flex-shrink-0 items-center gap-2 px-4 text-[0.8125rem] font-semibold whitespace-nowrap text-white transition-all [background:var(--grad-cyan)] ${
                    selectedIds.size === 0
                      ? 'cursor-not-allowed opacity-55'
                      : 'hover:-translate-y-px hover:[box-shadow:var(--tk-shadow-md),var(--tk-glow-sm)]'
                  }`}
                >
                  <Plus className="h-3.5 w-3.5" />
                  {selectedIds.size > 0
                    ? `Start campaign for ${String(selectedIds.size)}`
                    : 'Start campaign'}
                </button>
              </div>
            )}

            {!tellerMode && insightsOpen && (
              <ProductInsightsRow
                insights={quickInsights}
                appliedId={appliedInsight?.id ?? null}
                onSelect={toggleInsight}
                onHide={() => {
                  setInsightsOpen(false);
                }}
              />
            )}

            {/* v6 .ledger-area: relative positioning context; makes 436px of room for
          the drawer (420 + 16 gap) when open; drops it below 900px (D4/D5). */}
            <div
              className={`relative w-full transition-[padding-right] duration-200 ${
                inspectorCustomer ? 'pr-[436px] max-[900px]:pr-0' : ''
              }`}
            >
              <CustomerLedger
                customers={safeCustomers}
                filters={filters}
                selectedIds={selectedIds}
                selectable={!tellerMode}
                {...(tellerMode
                  ? {}
                  : // SCRUM-867: column filters are a campaigns-page affordance. The
                    // teller Customer Dashboard reuses this ledger and gets no filter
                    // controls, so its behaviour is unchanged (plan RT3).
                    { columnFilters, onColumnFiltersChange: setColumnFilters })}
                activeId={inspectorId}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onOpen={setInspectorId}
                inspecting={Boolean(inspectorCustomer)}
                header={
                  <LedgerToolbar
                    search={search}
                    onSearchChange={setSearch}
                    filteredCount={filteredCount}
                    totalCount={safeCustomers.length}
                  />
                }
              />

              {/* z-10, NOT the prototype's z-95: Ryan's .reasoning-inspector uses 95
            only to beat his inline .col-menu-backdrop (90) because HIS popovers
            render inside the drawer. Ours portal to <body> at z-50 (Radix), so
            95 would stack the drawer ABOVE every popover and swallow their
            clicks (caught by the refer-popover E2E). The drawer only needs to
            clear the ledger card within .ledger-area. */}
              {inspectorCustomer && (
                <div className="absolute inset-y-0 right-0 z-10 w-[420px] animate-[inspector-in_200ms_cubic-bezier(0.16,1,0.3,1)] max-[900px]:hidden">
                  <CustomerDrawer
                    customer={inspectorCustomer}
                    products={safeProducts}
                    tellerMode={tellerMode}
                    onClose={() => {
                      setInspectorId(null);
                    }}
                    onStartCampaign={openWizardForCustomer}
                  />
                </div>
              )}
            </div>

            {!tellerMode && (
              <SelectionToolbar
                count={selectedIds.size}
                onClear={() => {
                  setSelectedIds(new Set());
                }}
                onStartCampaign={openWizardForSelection}
              />
            )}

            {!tellerMode && wizardInit && (
              <CampaignWizard
                open
                init={wizardInit}
                customers={safeCustomers}
                products={safeProducts}
                onClose={() => {
                  setWizardInit(null);
                }}
                onExport={(payload) => {
                  const targets = safeCustomers.filter((c) => payload.targetIds.includes(c.id));
                  if (payload.exportFormat === 'copy') {
                    const text =
                      payload.previewTab === 'talk'
                        ? payload.talkTrack
                        : `${payload.emailSubject}\n\n${payload.emailBody}`;
                    void navigator.clipboard.writeText(text);
                    showToast(
                      `Copied ${payload.previewTab === 'talk' ? 'talk track' : 'email'} to clipboard.`,
                      'success',
                    );
                  } else {
                    const csv = buildCsv(targets, {
                      campaignName: payload.campaignName,
                      product: payload.product,
                      emailSubject: payload.emailSubject,
                      emailBody: payload.emailBody,
                      talkTrack: payload.talkTrack,
                    });
                    const filename = csvFilename(payload.campaignName);
                    downloadCsv(csv, filename);
                    showToast(
                      `${filename} downloaded · ${targets.length.toLocaleString('en-US')} rows ready for CRM import.`,
                      'success',
                    );
                    // SCRUM-531: persist the campaign + its targets for outcome tracking
                    // (SCRUM-71–75). Fired AFTER the download so a persist failure never blocks
                    // the export; best-effort. The attemptPersist helper owns success (refresh the
                    // SCRUM-71 selector) and failure (a Retry toast, SCRUM-794) so the initial send
                    // and every retry share one code path.
                    attemptPersist(payload.campaignName, payload.product, targets);
                  }
                  // SCRUM-348: the export completes the referral's outreach — flip
                  // the linked referral to campaign_sent (store + server).
                  if (wizardInit.linkedReferralId) {
                    useReferralsStore.getState().markCampaignSent(wizardInit.linkedReferralId);
                  }
                  setWizardInit(null);
                }}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * SCRUM-322 (audit F1): ToastProvider moved UP to the AppShell so toasts fired
 * from any page render. CampaignsPage no longer wraps itself — tests that
 * render CampaignsPage directly must provide the ToastProvider themselves.
 */
export function CampaignsPage(props: CampaignsPageProps = {}) {
  return <CampaignsPageInner {...props} />;
}
